import { CompanyOpenTicketCount, StaffTicket } from "@signalist/shared-types";

import { Companies } from "@/pages/Private/pages/Companies/schema/companies";

export type CustomerRole = "ADMIN" | "USER";

/** "Company admin" when the filer is one of the company's admins, else a plain user (decision 5). */
export const customerRole = (
	company: Pick<Companies, "admins"> | undefined,
	userId: number
): CustomerRole => (company?.admins.some((admin) => admin.id === userId) ? "ADMIN" : "USER");

/** Dates arrive as ISO strings at runtime although the schema says Date (web CLAUDE.md §6). */
const time = (value: Date | string | null | undefined): number =>
	value ? new Date(value).getTime() : 0;

/** The plan: the product of the newest subscription that has not ended; null when there is none. */
export const currentPlanName = (
	company: Pick<Companies, "subscriptions"> | undefined
): string | null =>
	(company?.subscriptions ?? [])
		.filter((subscription) => !subscription.endedAt)
		.sort((a, b) => time(b.startedAt) - time(a.startedAt))[0]?.product.name ?? null;

/**
 * The company's open tickets from `GET tickets/admin/counts` — a SQL aggregate, so a string at
 * runtime; 0 for a company without a row, null while the counts have not arrived.
 */
export const openTicketCount = (
	counts: readonly CompanyOpenTicketCount[] | undefined,
	companyId: number
): number | null => {
	if (!counts) {
		return null;
	}

	const row = counts.find((candidate) => candidate.companyId === companyId);

	return row ? Number(row.openCount ?? 0) || 0 : 0;
};

/** "Other tickets from <company>" shows this many rows (decision 7); the query asks for one more. */
export const OTHER_TICKETS_LIMIT = 5;

/** The company's queue page without the ticket being read, capped for the rail. */
export const otherCompanyTickets = (
	rows: readonly StaffTicket[],
	currentId: number
): StaffTicket[] => rows.filter((row) => row.id !== currentId).slice(0, OTHER_TICKETS_LIMIT);
